import { StateManager } from '../../managers/StateManager';
import { PixiAppRefs } from './types';

/** Positions of the selected elements captured when a group drag begins. */
export type DragAnchor = Map<string, { x: number; y: number }>;

/** A snapshot of an in-progress group drag: its primary element and start positions. */
export interface GroupDragState {
  primaryId: string;
  starts: DragAnchor;
}

/**
 * New positions for the non-primary members of a group drag, derived from how far
 * the primary element has moved from its start. Pure so it can be unit-tested
 * without a live PIXI scene.
 */
export function computeGroupMoves(
  starts: DragAnchor,
  primaryId: string,
  primaryX: number,
  primaryY: number
): Array<{ id: string; x: number; y: number }> {
  const anchor = starts.get(primaryId);
  if (!anchor) {
    return [];
  }
  const dx = primaryX - anchor.x;
  const dy = primaryY - anchor.y;

  const moves: Array<{ id: string; x: number; y: number }> = [];
  starts.forEach((start, id) => {
    if (id === primaryId) {
      return;
    }
    moves.push({ id, x: start.x + dx, y: start.y + dy });
  });
  return moves;
}

/** Captures the current positions of the given element ids from state. */
function snapshot(stateManager: StateManager, ids: string[]): DragAnchor {
  const state = stateManager.getState();
  const anchor: DragAnchor = new Map();
  ids.forEach(id => {
    const connector = state.connectors.find(c => c.id === id);
    if (connector) {
      anchor.set(id, { x: connector.xPosition, y: connector.yPosition });
      return;
    }
    const node = state.nodes.find(n => n.id === id);
    if (node) {
      anchor.set(id, { x: node.xPosition, y: node.yPosition });
    }
  });
  return anchor;
}

/** Moves a single element (connector or node) to a new position. */
function moveElement(stateManager: StateManager, id: string, x: number, y: number): void {
  const state = stateManager.getState();
  if (state.connectors.some(c => c.id === id)) {
    stateManager.updateConnector(id, { xPosition: x, yPosition: y });
    return;
  }
  if (state.nodes.some(n => n.id === id)) {
    stateManager.updateNode(id, { xPosition: x, yPosition: y });
  }
}

/**
 * Begins dragging the element with the given id. When it is part of a
 * multi-element selection the whole selection moves together; otherwise the drag
 * is solo and any lingering selection is cleared (grabbing outside a selection
 * deselects).
 */
export function beginEntityDrag(refs: PixiAppRefs, stateManager: StateManager, primaryId: string): void {
  const selection = stateManager.getSelection();
  if (selection.includes(primaryId) && selection.length > 1) {
    refs.groupDrag.current = { primaryId, starts: snapshot(stateManager, selection) };
    return;
  }
  refs.groupDrag.current = null;
  if (!selection.includes(primaryId) && selection.length > 0) {
    stateManager.setSelection([]);
  }
}

/** Drags the rest of the selection alongside the primary element as it moves. */
export function dragEntityGroup(refs: PixiAppRefs, stateManager: StateManager, primaryId: string, primaryX: number, primaryY: number): void {
  const group = refs.groupDrag.current;
  if (!group || group.primaryId !== primaryId) {
    return;
  }
  computeGroupMoves(group.starts, primaryId, primaryX, primaryY).forEach(move => {
    moveElement(stateManager, move.id, move.x, move.y);
  });
}

/** Ends a group drag, discarding the captured start positions. */
export function endEntityDrag(refs: PixiAppRefs): void {
  refs.groupDrag.current = null;
}
